import * as LocalStorage from "../local-storage";
import { logger } from "../tracking";

import type { AuthService } from "./context";
import type {
  AuthAction,
  CurrentAuthState,
  SessionInfo,
  UserInfo,
  OrgInfo,
} from "./state";
import {
  UNAUTHENTICATED,
  HYDRATING,
  REDIRECTING,
  LOADING_ORGS,
  AWAITING_PASSWORD,
  AWAITING_ORG_SELECT,
  EXCHANGING,
  AUTHENTICATING,
  CREATING_SESSION,
  AUTHENTICATED,
  AUTH_ERROR,
  toSessionInfo,
} from "./state";

// ── Effect result union ──

type EffectResult =
  | { kind: "noop" }
  | {
      kind: "hydrated";
      userInfo: UserInfo;
      locationId: string | null;
      sessionData: SessionInfo;
    }
  | { kind: "orgs_loaded"; email: string; orgs: OrgInfo[]; session?: string }
  | { kind: "authenticated"; userInfo: UserInfo }
  | { kind: "session_created"; sessionData: SessionInfo }
  | { kind: "error"; message: string }
  | { kind: "requires_logout" };

// ── Pure async effect implementation ──

async function runEffectAsync(
  state: CurrentAuthState,
  authService: AuthService,
): Promise<EffectResult> {
  switch (state.status) {
    case HYDRATING: {
      logger.info("[AuthEffect] refreshing user info");

      try {
        const userInfo = await authService.refreshUser({ token: state.token });
        logger.info("[AuthEffect] refresh success, creating session", {
          email: userInfo.email,
          orgCount: userInfo.orgs?.length,
          location: state.locationId,
        });

        LocalStorage.storeIdentifiers({ userToken: userInfo.token });

        const rawSessionData = await authService.createSession({
          locationId: state.locationId,
          token: userInfo.token!,
        });

        const sessionData = toSessionInfo(rawSessionData);
        logger.info("[AuthEffect] session refreshed", {
          hasAaaToken: !!sessionData.aaaToken,
        });

        return {
          kind: "hydrated",
          userInfo,
          locationId: state.locationId,
          sessionData,
        };
      } catch (err) {
        logger.warn("[AuthEffect] hydration failed, clearing session", { err });
        return { kind: "requires_logout" };
      }
    }

    case REDIRECTING: {
      logger.info("[AuthEffect] calling redirect");
      const userInfo = await authService.redirect({
        token: state.token,
        tokenType: state.tokenType,
      });
      logger.info("[AuthEffect] redirect response", {
        email: userInfo.email,
        orgCount: userInfo.orgs?.length,
        hasSession: !!userInfo.session,
      });

      return {
        kind: "orgs_loaded",
        email: userInfo.email ?? "",
        orgs: userInfo.orgs ?? [],
        session: userInfo.session,
      };
    }

    case LOADING_ORGS: {
      logger.info("[AuthEffect] loading orgs", { email: state.email });
      const data = await authService.getOrgsForUser({ email: state.email });

      return {
        kind: "orgs_loaded",
        email: state.email,
        orgs: data.orgs ?? [],
      };
    }

    case EXCHANGING: {
      logger.info("[AuthEffect] exchanging session", { orgId: state.orgId });
      const userInfo = await authService.exchange({
        orgId: state.orgId,
        session: state.session,
      });
      logger.info("[AuthEffect] exchange response", {
        email: userInfo.email,
        token: userInfo.token ? "present" : "missing",
        orgCount: userInfo.orgs?.length,
      });

      return { kind: "authenticated", userInfo };
    }

    case AUTHENTICATING: {
      logger.info("[AuthEffect] authenticating with password");
      const userInfo = await authService.authenticateUser({
        email: state.email,
        orgId: state.orgId,
        password: state.password,
      });

      return { kind: "authenticated", userInfo };
    }

    case CREATING_SESSION: {
      logger.info("[AuthEffect] creating session", {
        locationId: state.locationId,
      });
      const rawSessionData = await authService.createSession({
        locationId: state.locationId,
        token: state.token,
      });
      const sessionData = toSessionInfo(rawSessionData);
      logger.info("[AuthEffect] session created", {
        hasAaaToken: !!sessionData.aaaToken,
      });

      return { kind: "session_created", sessionData };
    }

    case UNAUTHENTICATED:
    case AWAITING_PASSWORD:
    case AWAITING_ORG_SELECT:
    case AUTHENTICATED:
    case AUTH_ERROR:
      return { kind: "noop" };
  }
}

// ── Result → action mapping ──

const ERROR_MESSAGES: Record<string, string> = {
  [REDIRECTING.toString()]: "Failed to authenticate",
  [LOADING_ORGS.toString()]: "Failed to load organizations",
  [EXCHANGING.toString()]: "Failed to authenticate",
  [AUTHENTICATING.toString()]: "Invalid credentials",
  [CREATING_SESSION.toString()]: "Failed to create session",
};

function resultToAction(
  result: Exclude<EffectResult, { kind: "noop" }>,
): AuthAction {
  switch (result.kind) {
    case "hydrated":
      return {
        type: "REFRESH_SUCCESS",
        userInfo: result.userInfo,
        locationId: result.locationId,
        sessionData: result.sessionData,
      };
    case "orgs_loaded":
      return {
        type: "ORGS_LOADED",
        email: result.email,
        orgs: result.orgs,
        session: result.session,
      };
    case "authenticated":
      return { type: "AUTH_SUCCESS", userInfo: result.userInfo };
    case "session_created":
      return { type: "SESSION_CREATED", sessionData: result.sessionData };
    case "error":
      return { type: "ERROR", message: result.message };
    case "requires_logout":
      return { type: "LOGOUT" };
  }
}

// ── Public entry point ──

/**
 * Async side-effect counterpart to the reducer.
 *
 * The reducer handles pure, synchronous state transitions.
 * This function handles the async consequences of each state -
 * calling the AuthService and dispatching result actions.
 *
 * Returns an AbortController so the caller can cancel in-flight
 * work when the state changes before a request completes.
 */
export function runEffect(
  state: CurrentAuthState,
  authService: AuthService,
  dispatch: (action: AuthAction) => void,
): AbortController {
  const controller = new AbortController();

  runEffectAsync(state, authService)
    .then((result) => {
      if (controller.signal.aborted || result.kind === "noop") return;
      dispatch(resultToAction(result));
    })
    .catch((err) => {
      if (controller.signal.aborted) return;
      const message =
        ERROR_MESSAGES[state.status.toString()] ?? "Unknown error";
      logger.error(`[AuthEffect] ${state.status.toString()} failed`, err);
      dispatch({ type: "ERROR", message });
    });

  return controller;
}
